import {
  Box,
  ColumnLayout,
  ExpandableSection,
  Icon,
  IconProps,
  Link,
  SpaceBetween,
} from '@amzn/awsui-components-console';

// A dismissible-style getting-started orientation for service-team content
// authors, shown on the Launch list between the Filter and Conference tasks.
// It folds down to just its header. The four steps mirror the standard author
// workflow (find task -> add packages & build -> preview -> mark ready), each
// linking to the relevant Fusion User Guide section for deeper help.
//
// Notes on component choices:
// - Cloudscape has no dedicated horizontal "sequence"/steps display component
//   (Wizard is a multi-step form flow, not a static diagram), so ColumnLayout
//   with columns={4} is the idiomatic way to evenly divide the width.
// - The console icon set has no literal table/building-blocks/laptop/rocket
//   glyphs; each step uses the closest appropriate standard icon.
interface GettingStartedStep {
  icon: IconProps.Name;
  name: string;
  description: string;
  linkText: string;
  linkHref: string;
}

const FUG_BASE = 'https://alpha.www.docs.aws.a2z.com/fusion/latest/userguide';

const STEPS: GettingStartedStep[] = [
  {
    icon: 'list-view',
    name: 'Step 1: Find your task below',
    description: 'Select a launch below to get started.',
    linkText: 'How do I get a task for my launch?',
    linkHref: `${FUG_BASE}/flc-conferences-overview.html#flc-nominating-launch`,
  },
  {
    icon: 'insert-row',
    name: 'Step 2: Add packages and build',
    description:
      'Add doc packages at the commits with your changes, then submit to build.',
    linkText: 'How do I enter my docs packages?',
    linkHref: `${FUG_BASE}/flc-using-flc.html#flc-enter-build-info`,
  },
  {
    icon: 'zoom-to-fit',
    name: 'Step 3: Preview and verify',
    description:
      'Wait for builds to finish, then preview your content on the venue endpoint.',
    linkText: 'How do I view a preview?',
    linkHref: `${FUG_BASE}/flc-using-flc.html#flc-preview-verify`,
  },
  {
    icon: 'lock-private',
    name: 'Step 4: Mark Ready to Publish',
    description:
      "Lock the task once it's final, before the Final build deadline (ELTB).",
    linkText: 'How is conference documentation released?',
    linkHref: `${FUG_BASE}/flc-release-process.html`,
  },
];

// Expanded by default so first-time authors see the workflow; collapsible to a
// single header row once they know it.
export const GettingStartedPanel = () => (
  <ExpandableSection
    variant='container'
    headerText='Get your docs launch-ready'
    defaultExpanded={true}
  >
    <ColumnLayout columns={4} variant='text-grid'>
      {STEPS.map((step) => (
        <SpaceBetween key={step.name} size='xs'>
          <Icon name={step.icon} size='large' />
          <Box fontWeight='bold'>{step.name}</Box>
          <Box variant='p'>{step.description}</Box>
          <Link external href={step.linkHref}>
            {step.linkText}
          </Link>
        </SpaceBetween>
      ))}
    </ColumnLayout>
  </ExpandableSection>
);
